import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Lock, Cloud, Zap, Loader2, PlugZap, CheckCircle2, KeyRound, ListChecks, Plus, Trash2 } from "lucide-react";

export default function Settings() {
  const [provider, setProvider] = useState("emergent");
  const [azure, setAzure] = useState({ endpoint: "", api_key: "", deployment: "", api_version: "2024-08-01-preview" });
  const [keySet, setKeySet] = useState(false);
  const [keyMasked, setKeyMasked] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [passcodeSet, setPasscodeSet] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [curPass, setCurPass] = useState("");
  const [savingPass, setSavingPass] = useState(false);

  const [skuItems, setSkuItems] = useState([]);
  const [savingSku, setSavingSku] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: pc }, { data: cat }] = await Promise.all([
        api.get("/settings/ai"),
        api.get("/admin/passcode-status"),
        api.get("/sku-catalog"),
      ]);
      setProvider(data.provider);
      setAzure({
        endpoint: data.azure.endpoint || "",
        api_key: "",
        deployment: data.azure.deployment || "",
        api_version: data.azure.api_version || "2024-08-01-preview",
      });
      setKeySet(data.azure.api_key_set);
      setKeyMasked(data.azure.api_key_masked);
      setPasscodeSet(pc.is_set);
      setSkuItems(cat.items || []);
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addSkuRow = () => setSkuItems([...skuItems, { id: `new-${Date.now()}`, sku: "", description: "", default_qty: 0 }]);
  const updateSkuRow = (i, patch) => setSkuItems(skuItems.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeSkuRow = (i) => setSkuItems(skuItems.filter((_, idx) => idx !== i));
  const saveSkuCatalog = async () => {
    const items = skuItems.filter((it) => (it.sku || "").trim());
    setSavingSku(true);
    try {
      const { data } = await api.put("/admin/sku-catalog", { items: items.map((it) => ({ ...it, default_qty: Number(it.default_qty || 0) })) });
      setSkuItems(data.items || []);
      toast.success("SKU catalog saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save catalog"); }
    finally { setSavingSku(false); }
  };

  const save = async () => {
    if (passcodeSet && !passcode.trim()) { toast.error("Enter the admin passcode to save changes"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/ai", { provider, azure, passcode });
      toast.success("AI settings saved");
      setKeySet(data.azure.api_key_set);
      setKeyMasked(data.azure.api_key_masked);
      setAzure((a) => ({ ...a, api_key: "" }));
      setPasscode("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const savePasscode = async () => {
    if (newPass.length < 4) { toast.error("Passcode must be at least 4 characters"); return; }
    setSavingPass(true);
    try {
      await api.post("/admin/passcode/set", { new_passcode: newPass, current_passcode: curPass || null });
      toast.success(passcodeSet ? "Passcode updated" : "Admin passcode set — settings are now locked");
      setPasscodeSet(true); setNewPass(""); setCurPass("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set passcode"); }
    finally { setSavingPass(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/ai/test", azure);
      if (data.success) toast.success(`Azure connection OK — response: "${data.message}"`);
      else toast.error(`Azure test failed: ${data.message}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="p-10 font-mono-jb text-[#8B949E]">Loading...</div>;

  return (
    <div className="p-8 lg:p-10 max-w-3xl">
      <div className="border-b border-white/10 pb-6 mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Admin</div>
        <h1 className="font-mono-jb text-3xl sm:text-4xl font-extrabold tracking-tighter text-white flex items-center gap-3">
          <ShieldCheck className="text-[#00F0FF]" size={34} /> AI Engine Settings
        </h1>
        <p className="text-[#8B949E] mt-3 leading-relaxed">Control which AI backend generates your documentation. Keep data private by routing generation through your organisation's <span className="text-white">Azure OpenAI</span> deployment.</p>
      </div>

      <section className="border border-white/10 bg-[#0B0C10] p-6 scanlines">
        <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb flex items-center gap-2 mb-3">
          <PlugZap size={14} className="text-[#00F0FF]" /> AI Provider
        </label>
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger data-testid="provider-select" className="bg-[#0B0C10] border-white/15 font-mono-jb h-12 focus:ring-[#00F0FF]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0B0C10] border-white/15 text-white">
            <SelectItem value="emergent" data-testid="provider-option-emergent" className="font-mono-jb">
              <span className="inline-flex items-center gap-2"><Zap size={14} className="text-[#EAE04A]" /> Default AI Engine (managed)</span>
            </SelectItem>
            <SelectItem value="azure" data-testid="provider-option-azure" className="font-mono-jb">
              <span className="inline-flex items-center gap-2"><Cloud size={14} className="text-[#00F0FF]" /> Azure OpenAI — Private / Secure</span>
            </SelectItem>
          </SelectContent>
        </Select>

        <div className="mt-3 text-xs font-mono-jb">
          {provider === "azure" ? (
            <span className="text-[#00FF41] flex items-center gap-1.5"><Lock size={12} /> Generation stays inside your Azure tenant. No public OpenAI is used.</span>
          ) : (
            <span className="text-[#8B949E]">Uses the managed engine so you can start immediately. Switch to Azure for private deployments.</span>
          )}
        </div>
      </section>

      {provider === "azure" && (
        <section className="border border-[#00F0FF]/20 bg-[#0B0C10] p-6 mt-6 fade-up">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="text-[#00F0FF]" size={16} />
            <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Azure OpenAI Credentials</h2>
          </div>
          <div className="space-y-4">
            <Field label="Endpoint" testid="azure-endpoint" placeholder="https://your-resource.openai.azure.com/"
              value={azure.endpoint} onChange={(v) => setAzure({ ...azure, endpoint: v })} />
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">API Key</label>
              <Input data-testid="azure-api-key" type="password" value={azure.api_key} onChange={(e) => setAzure({ ...azure, api_key: e.target.value })}
                placeholder={keySet ? `Saved (${keyMasked}) — leave blank to keep` : "Enter Azure API key"}
                className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
              {keySet && <div className="text-[10px] text-[#00FF41] font-mono-jb mt-1 flex items-center gap-1"><CheckCircle2 size={11} /> A key is stored securely.</div>}
            </div>
            <Field label="Deployment Name" testid="azure-deployment" placeholder="gpt-4o"
              value={azure.deployment} onChange={(v) => setAzure({ ...azure, deployment: v })} />
            <Field label="API Version" testid="azure-api-version" placeholder="2024-08-01-preview"
              value={azure.api_version} onChange={(v) => setAzure({ ...azure, api_version: v })} />
          </div>

          <div className="mt-5 flex gap-3">
            <Button data-testid="test-azure-button" onClick={testConnection} disabled={testing} variant="outline"
              className="rounded-sm border-[#00F0FF]/40 bg-transparent text-[#00F0FF] hover:bg-[#00F0FF]/10 hover:text-[#00F0FF] font-mono-jb uppercase text-xs tracking-widest">
              {testing ? <Loader2 className="animate-spin" size={15} /> : <><PlugZap size={14} className="mr-1" /> Test Connection</>}
            </Button>
          </div>

          <div className="mt-5 border-t border-white/10 pt-4 text-[11px] text-[#8B949E] font-mono-jb leading-relaxed">
            <span className="text-white uppercase tracking-wider">Ask your Azure admin for:</span><br />
            1. Endpoint URL &nbsp; 2. API Key &nbsp; 3. Deployment name (deployed model) &nbsp; 4. API version
          </div>
        </section>
      )}

      <section className="border border-white/10 bg-[#0B0C10] p-6 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="text-[#EAE04A]" size={16} />
          <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Admin Passcode Lock</h2>
        </div>
        <p className="text-[#8B949E] text-xs mb-5 leading-relaxed">
          {passcodeSet
            ? "A passcode is active — changing the AI settings above requires it."
            : "Set a passcode so only you can change these AI settings. Once set, saving requires the passcode."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {passcodeSet && (
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Current Passcode</label>
              <Input data-testid="current-passcode-input" type="password" value={curPass} onChange={(e) => setCurPass(e.target.value)}
                placeholder="••••" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
            </div>
          )}
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">{passcodeSet ? "New Passcode" : "Set Passcode"}</label>
            <Input data-testid="new-passcode-input" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)}
              placeholder="min 4 characters" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
          </div>
        </div>
        <Button data-testid="save-passcode-button" onClick={savePasscode} disabled={savingPass}
          className="mt-4 rounded-sm bg-[#EAE04A] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#EAE04A] transition-colors duration-200">
          {savingPass ? <Loader2 className="animate-spin" size={15} /> : (passcodeSet ? "Update Passcode" : "Set Passcode")}
        </Button>
      </section>

      <section className="border border-white/10 bg-[#0B0C10] p-6 mt-6" data-testid="sku-catalog-section">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className="text-[#00F0FF]" size={16} />
          <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Standard SKU Catalog</h2>
        </div>
        <p className="text-[#8B949E] text-xs mb-5 leading-relaxed">Professional-services line items engineers can tick and add to a proposal's Bill of Materials. SKU + description + default quantity.</p>
        <div className="space-y-3">
          {skuItems.map((it, i) => (
            <div key={it.id || i} data-testid={`catalog-row-${i}`} className="grid grid-cols-12 gap-2 items-start border border-white/10 p-3 rounded-sm">
              <div className="col-span-3">
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">SKU</label>
                <Input data-testid={`catalog-sku-${i}`} value={it.sku} onChange={(e) => updateSkuRow(i, { sku: e.target.value })}
                  placeholder="SX-..." className="mt-1 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
              </div>
              <div className="col-span-3">
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Category</label>
                <Input data-testid={`catalog-cat-${i}`} value={it.category || ""} onChange={(e) => updateSkuRow(i, { category: e.target.value })}
                  placeholder="e.g. Deployment & Delivery" className="mt-1 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
              </div>
              <div className="col-span-4">
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Description</label>
                <Input data-testid={`catalog-desc-${i}`} value={it.description} onChange={(e) => updateSkuRow(i, { description: e.target.value })}
                  placeholder="Line item description" className="mt-1 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
              </div>
              <div className="col-span-1">
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Qty</label>
                <Input data-testid={`catalog-qty-${i}`} type="number" min="0" value={it.default_qty} onChange={(e) => updateSkuRow(i, { default_qty: e.target.value })}
                  className="mt-1 bg-[#0B0C10] border-white/15 font-mono-jb text-sm text-center focus-visible:ring-[#00F0FF]" />
              </div>
              <div className="col-span-1 flex justify-end pt-6">
                <button data-testid={`catalog-remove-${i}`} onClick={() => removeSkuRow(i)} className="text-[#8B949E] hover:text-[#FF3B30] transition-colors" title="Remove">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-3">
          <Button data-testid="add-sku-button" onClick={addSkuRow} variant="outline"
            className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF] font-mono-jb uppercase text-xs tracking-widest">
            <Plus size={14} className="mr-1" /> Add SKU
          </Button>
          <Button data-testid="save-sku-catalog-button" onClick={saveSkuCatalog} disabled={savingSku}
            className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
            {savingSku ? <Loader2 className="animate-spin" size={15} /> : "Save Catalog"}
          </Button>
        </div>
      </section>

      <div className="mt-6 border-t border-white/10 pt-6">
        {passcodeSet && (
          <div className="mb-4 max-w-xs">
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#EAE04A] font-mono-jb flex items-center gap-1.5">
              <Lock size={11} /> Admin Passcode (required to save)
            </label>
            <Input data-testid="save-passcode-verify-input" type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)}
              placeholder="Enter passcode" className="mt-2 bg-[#0B0C10] border-[#EAE04A]/40 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
          </div>
        )}
        <Button data-testid="save-settings-button" onClick={save} disabled={saving}
          className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF] hover:glow-cyan transition-colors duration-200 h-11 px-6">
          {saving ? <Loader2 className="animate-spin" size={16} /> : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, testid, placeholder, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">{label}</label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
    </div>
  );
}
